import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Plus } from 'lucide-react';
import { useSnackbar } from 'notistack';

import { isSignedIn, useAuth } from '@/features/scaffolding/components/AuthContext';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import useConfirmationDialogContext from '@/shared/hooks/useConfirmationDialogContext';
import AppPage from '@/shared/components/PageComponents/AppPage';
import { Loading } from '@/shared/components';

import useTranscodingProfilesQuery, {
  type TranscodingProfile,
  type TranscodingProfileOutput,
} from '../hooks/useTranscodingProfilesQuery';
import useCreateTranscodingProfileMutation, {
  type TranscodingProfileInput,
} from '../hooks/useCreateTranscodingProfileMutation';
import useUpdateTranscodingProfileMutation from '../hooks/useUpdateTranscodingProfileMutation';
import useDeleteTranscodingProfileMutation from '../hooks/useDeleteTranscodingProfileMutation';
import { type OutputProfileMode } from '../hooks/useOutputProfilesQuery';
import useSetOutputProfileAssignmentMutation from '../hooks/useSetOutputProfileAssignmentMutation';
import PageHeader from '@/shared/components/PageHeader';
import { ROW_ACTIONS_SX, TOOLBAR_ACTIONS_SX } from '@/shared/constants/tableIconButtons';
import { cx } from '@/styles/muiTheme';

const REQUIRED_PERMISSIONS = ['menu:admin:profiles'];

// Human-friendly labels for the player-config modes a profile can be assigned
// to. Mirrors OUTPUT_PROFILE_MODES on the backend.
const MODE_LABELS: Record<string, string> = {
  interactive: 'Interactive player',
  clip_export_default: 'Clip export',
  clip_export_portrait: 'Clip export (Portrait)',
  wsc: 'WSC streamer',
};

// The assignable targets, in display order. Mirrors OUTPUT_PROFILE_MODES on the
// backend; each target points at exactly one profile (assignment is exclusive).
const ASSIGNABLE_MODES: OutputProfileMode[] = ['interactive', 'clip_export_default','clip_export_portrait', 'wsc'];

// Encoder `tune` codes (as stored in output.tune) → display label, theme colour,
// and the NVENC tuning-info doc description shown in the dropdown. Using MUI
// palette names keeps the chips correct in dark mode. Mirrors the NvEncTuningInfo
// enum (NV_ENC_TUNING_INFO_*).
const TUNE_META: Record<
  string,
  { label: string; description: string; color: 'success' | 'info' | 'warning' | 'secondary' | 'primary' }
> = {
  LowLatency: {
    label: 'Low-Latency',
    description: 'Tune presets for low latency streaming.',
    color: 'success',
  },
  HighQuality: {
    label: 'High-Quality',
    description: 'Tune presets for latency tolerant encoding.',
    color: 'info',
  },
  UltraLowLatency: {
    label: 'Ultra-Low-Latency',
    description: 'Tune presets for ultra low latency streaming.',
    color: 'warning',
  },
  Lossless: {
    label: 'Lossless',
    description: 'Tune presets for lossless encoding.',
    color: 'secondary',
  },
  UltraHighQuality: {
    label: 'Ultra-High-Quality',
    description:
      'Tune presets for latency tolerant encoding for higher quality. Only supported for HEVC on Turing+ architectures.',
    color: 'primary',
  },
};

const TUNE_OPTIONS = Object.entries(TUNE_META).map(([value, { label, description }]) => ({
  value,
  label,
  description,
}));

// Output codecs the player accepts. `h264` is the documented default; `hevc`
// unlocks the Ultra-High-Quality tune (Turing+). Unrecognised stored values are
// preserved in the dropdown so a profile is never silently downgraded on save.
const CODEC_OPTIONS = [
  { value: 'h264', label: 'H.264 / AVC' },
  { value: 'h265', label: 'H.265 / HEVC' },
];

// ---------------------------------------------------------------------------
// Derived-display helpers — the detail view shows friendly values computed from
// the raw `output` block (the source of truth stays the stored JSON).
// ---------------------------------------------------------------------------

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

const aspectRatio = (width?: number, height?: number): string => {
  if (!width || !height) return '—';
  const g = gcd(width, height) || 1;
  return `${width / g}:${height / g}`;
};

// fps is a rational [numerator, denominator] (e.g. [60000, 1001] = 59.94) so we
// can represent broadcast fractional rates exactly. Render integers cleanly,
// everything else to 2 dp.
const fpsDecimal = (fps?: number[]): string => {
  if (!fps || fps.length === 0) return '—';
  if (fps.length === 1 || !fps[1]) return String(fps[0]);
  const value = fps[0] / fps[1];
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const mbps = (bps?: number): string => {
  if (bps == null) return '—';
  const value = bps / 1_000_000;
  return `${Number.isInteger(value) ? value : Number(value.toFixed(2))} Mbps`;
};

const resolutionLabel = (output?: TranscodingProfileOutput): string =>
  output?.res ? `${output.res.width} × ${output.res.height}` : '—';

const errorMessage = (error: unknown, fallback: string): string => {
  const e = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;
};

// End-adornment "clear" button for optional encoder fields. Shown only when the
// field holds a value; clearing it makes buildOutput drop the key entirely, so
// the encoder falls back to its own default rather than persisting a 0/empty.
const clearAdornment = (show: boolean, onClear: () => void) =>
  show ? (
    <InputAdornment position="end">
      <Tooltip title="Clear — use encoder default">
        <IconButton size="small" edge="end" aria-label="Clear value" onClick={onClear}>
          <ClearIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </InputAdornment>
  ) : undefined;

const TuneChip = ({ tune }: { tune?: unknown }) => {
  if (typeof tune !== 'string' || tune === '') {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  const meta = TUNE_META[tune];
  return (
    <Chip
      size="small"
      variant="outlined"
      color={meta?.color ?? 'default'}
      label={meta?.label ?? tune}
    />
  );
};

const StatCard = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <Paper variant="outlined" sx={{ p: 2, backgroundColor: cx.bg.elevated }}>
    <Typography variant="overline" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary">
        {sub}
      </Typography>
    )}
  </Paper>
);

// Editable "Targets" cell — assigns this profile to one or more player-config
// modes. Assignment is exclusive per target (a mode points at exactly one
// profile), so checking a target here *claims* it, moving it off whatever
// profile previously held it. Clearing a target to "none" is not supported
// (assign/move only): a target already on THIS profile is rendered
// checked-and-disabled, so the only mutation this control can fire is an assign.
const TargetAssignmentSelect = ({
  profile,
  disabled,
  onAssign,
}: {
  profile: TranscodingProfile;
  disabled: boolean;
  onAssign: (mode: OutputProfileMode) => void;
}) => {
  const assigned = profile.assignedModes;
  return (
    <Select
      multiple
      size="small"
      displayEmpty
      value={assigned}
      disabled={disabled}
      onChange={(e) => {
        const next = e.target.value as OutputProfileMode[];
        // Act only on a newly-checked target; unchecks are ignored (no unassign).
        const added = next.find((m) => !assigned.includes(m));
        if (added) onAssign(added);
      }}
      renderValue={(selected) =>
        (selected as OutputProfileMode[]).length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            Unassigned
          </Typography>
        ) : (
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {(selected as OutputProfileMode[]).map((m) => (
              <Chip key={m} size="small" label={MODE_LABELS[m] ?? m} />
            ))}
          </Stack>
        )
      }
      sx={{ minWidth: 170 }}
    >
      {ASSIGNABLE_MODES.map((m) => {
        const onThisProfile = assigned.includes(m);
        return (
          // Disabled when already on this profile — enforces assign/move-only.
          <MenuItem key={m} value={m} disabled={onThisProfile}>
            <Checkbox checked={onThisProfile} size="small" />
            <ListItemText primary={MODE_LABELS[m] ?? m} />
          </MenuItem>
        );
      })}
    </Select>
  );
};

// ---------------------------------------------------------------------------
// Edit/create form state. Numeric inputs are kept as strings so the fields can
// be cleared mid-edit without NaN flicker; they are parsed on save.
// ---------------------------------------------------------------------------

interface FormState {
  name: string;
  displayName: string;
  width: string;
  height: string;
  fpsNum: string;
  fpsDen: string;
  tune: string;
  avgBitrate: string;
  maxBitrate: string;
  // Advanced encoder knobs — all optional; empty ⇒ omitted so the player falls
  // back to its mode-specific default.
  codec: string;
  preset: string;
  gopLength: string;
  nPs: string;
}

const blankForm = (): FormState => ({
  name: '',
  displayName: '',
  width: '1920',
  height: '1080',
  fpsNum: '60000',
  fpsDen: '1001',
  tune: 'LowLatency',
  avgBitrate: '',
  maxBitrate: '',
  codec: '',
  preset: '',
  gopLength: '',
  nPs: '',
});

const formFromProfile = (profile: TranscodingProfile): FormState => {
  const o = profile.output;
  return {
    name: profile.name,
    displayName: profile.displayName ?? '',
    width: o?.res?.width != null ? String(o.res.width) : '',
    height: o?.res?.height != null ? String(o.res.height) : '',
    fpsNum: o?.fps?.[0] != null ? String(o.fps[0]) : '',
    fpsDen: o?.fps?.[1] != null ? String(o.fps[1]) : '',
    tune: typeof o?.tune === 'string' ? o.tune : '',
    avgBitrate: o?.avg_bitrate != null ? String(o.avg_bitrate) : '',
    maxBitrate: o?.max_bitrate != null ? String(o.max_bitrate) : '',
    codec: typeof o?.codec === 'string' ? o.codec : '',
    preset: o?.preset != null ? String(o.preset) : '',
    gopLength: o?.gop_length != null ? String(o.gop_length) : '',
    nPs: o?.n_ps != null ? String(o.n_ps) : '',
  };
};

// Rebuild the output block from the form, preserving any passthrough keys the
// form still does not surface (pipeline, ...) on the original profile.
const buildOutput = (
  base: TranscodingProfileOutput | undefined,
  form: FormState,
): TranscodingProfileOutput => {
  const output: TranscodingProfileOutput = {
    ...(base ?? {}),
    res: { width: Number(form.width), height: Number(form.height) },
    fps: [Number(form.fpsNum), Number(form.fpsDen)],
  };
  if (form.tune.trim() !== '') output.tune = form.tune;
  else delete output.tune;
  if (form.avgBitrate.trim() !== '') output.avg_bitrate = Number(form.avgBitrate);
  else delete output.avg_bitrate;
  if (form.maxBitrate.trim() !== '') output.max_bitrate = Number(form.maxBitrate);
  else delete output.max_bitrate;
  // Advanced — set when provided, drop when cleared (so the player uses its
  // mode default rather than persisting an empty/zero knob).
  if (form.codec.trim() !== '') output.codec = form.codec.trim();
  else delete output.codec;
  if (form.preset.trim() !== '') output.preset = Number(form.preset);
  else delete output.preset;
  if (form.gopLength.trim() !== '') output.gop_length = Number(form.gopLength);
  else delete output.gop_length;
  if (form.nPs.trim() !== '') output.n_ps = Number(form.nPs);
  else delete output.n_ps;
  return output;
};

const posNumber = (s: string): boolean => {
  const n = Number(s);
  return s.trim() !== '' && Number.isFinite(n) && n > 0;
};

type Mode = 'view' | 'edit' | 'create';

const ManageTranscodingProfilesPage = () => {
  const title = 'Transcoding Profiles';
  useDocumentTitle(title);

  const { sessionState } = useAuth();
  const isAuthReady = isSignedIn(sessionState);

  const snackBar = useSnackbar();
  const setConfirmationDialog = useConfirmationDialogContext();

  const profilesQuery = useTranscodingProfilesQuery();
  const createMutation = useCreateTranscodingProfileMutation();
  const updateMutation = useUpdateTranscodingProfileMutation();
  const deleteMutation = useDeleteTranscodingProfileMutation();
  const assignMutation = useSetOutputProfileAssignmentMutation();

  const profiles = profilesQuery.data ?? [];

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  const [form, setForm] = useState<FormState>(blankForm);

  // Default the selection to the first profile (mirrors the design, where one
  // profile is open on load). Skip while creating so the form is not stolen.
  useEffect(() => {
    if (mode === 'create') return;
    if (selectedId == null && profiles.length > 0) {
      setSelectedId(profiles[0].id);
    }
  }, [profiles, selectedId, mode]);

  const selected = profiles.find((p) => p.id === selectedId) ?? null;

  const setField =
    (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  // Reset an optional field to empty → buildOutput omits the key → encoder default.
  const clearField = (key: keyof FormState) => () => setForm((f) => ({ ...f, [key]: '' }));

  const startCreate = () => {
    setForm(blankForm());
    setSelectedId(null);
    setMode('create');
  };

  const startEdit = (profile: TranscodingProfile) => {
    setForm(formFromProfile(profile));
    setSelectedId(profile.id);
    setMode('edit');
  };

  const selectProfile = (profile: TranscodingProfile) => {
    setSelectedId(profile.id);
    setMode('view');
  };

  const isFormValid =
    form.displayName.trim() !== '' &&
    form.name.trim() !== '' &&
    posNumber(form.width) &&
    posNumber(form.height) &&
    posNumber(form.fpsNum) &&
    posNumber(form.fpsDen);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    const input: TranscodingProfileInput = {
      name: form.name.trim(),
      displayName: form.displayName.trim(),
      output: buildOutput(selected?.output, form),
    };

    try {
      if (mode === 'create') {
        const created = await createMutation.mutateAsync(input);
        setSelectedId(created.id);
        snackBar.enqueueSnackbar(`Profile "${input.displayName}" created`, {
          variant: 'success',
          autoHideDuration: 2000,
        });
      } else if (selected) {
        await updateMutation.mutateAsync({ id: selected.id, ...input });
        snackBar.enqueueSnackbar(`Profile "${input.displayName}" saved`, {
          variant: 'success',
          autoHideDuration: 2000,
        });
      }
      setMode('view');
    } catch (error) {
      snackBar.enqueueSnackbar(errorMessage(error, 'Failed to save profile'), {
        variant: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  const handleDelete = (profile: TranscodingProfile) => {
    setConfirmationDialog({
      confirmButtonStyle: 'error',
      title: 'Delete Transcoding Profile',
      message: (
        <Typography>
          Profile
          <Typography component="span" sx={{ fontWeight: 700, mx: 1 }}>
            “{profile.displayName ?? profile.name}”
          </Typography>
          will be permanently deleted. This action cannot be undone.
        </Typography>
      ),
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(profile.id);
          if (selectedId === profile.id) {
            setSelectedId(null);
            setMode('view');
          }
          snackBar.enqueueSnackbar(`Profile "${profile.displayName ?? profile.name}" deleted`, {
            variant: 'success',
            autoHideDuration: 2000,
          });
        } catch (error) {
          // Backstop: a profile assigned between load and delete returns 409.
          snackBar.enqueueSnackbar(errorMessage(error, 'Failed to delete profile'), {
            variant: 'error',
            autoHideDuration: 4000,
          });
        }
      },
    });
  };

  // Point a target (player-config mode) at this profile. The exclusive
  // mode→profile constraint means this also moves the target off its previous
  // profile; the mutation invalidates the profiles query so every row's Targets
  // column reflects the new ownership.
  const handleAssign = async (profile: TranscodingProfile, mode: OutputProfileMode) => {
    try {
      await assignMutation.mutateAsync({ mode, profileId: profile.id });
      snackBar.enqueueSnackbar(
        `“${profile.displayName ?? profile.name}” set as ${MODE_LABELS[mode] ?? mode} target`,
        { variant: 'success', autoHideDuration: 2000 },
      );
    } catch (error) {
      snackBar.enqueueSnackbar(errorMessage(error, 'Failed to assign target'), {
        variant: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  const columns = useMemo<MRT_ColumnDef<TranscodingProfile>[]>(
    () => [
      {
        id: 'profile',
        header: 'Profile',
        size: 220,
        accessorFn: (row) => row.displayName ?? row.name,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.original.displayName ?? row.original.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: cx.text.muted, fontFamily: cx.fontMono }}
            >
              {row.original.name}
            </Typography>
          </Box>
        ),
      },
      {
        id: 'resolution',
        header: 'Resolution',
        size: 120,
        accessorFn: (row) => resolutionLabel(row.output),
      },
      {
        id: 'tune',
        header: 'Tune',
        size: 140,
        accessorFn: (row) => (typeof row.output?.tune === 'string' ? row.output.tune : ''),
        Cell: ({ row }) => <TuneChip tune={row.original.output?.tune} />,
      },
      {
        id: 'targets',
        header: 'Targets',
        size: 200,
        enableSorting: false,
        // stopPropagation so opening the select does not also select the row —
        // the row's own onClick sits on muiTableBodyRowProps below.
        Cell: ({ row }) => (
          <Box onClick={(e) => e.stopPropagation()}>
            <TargetAssignmentSelect
              profile={row.original}
              disabled={assignMutation.isPending}
              onAssign={(mode) => handleAssign(row.original, mode)}
            />
          </Box>
        ),
      },
    ],
    [assignMutation.isPending, handleAssign],
  );

  if (!isAuthReady || profilesQuery.isLoading) {
    return (
      <AppPage title={title} requiredPermissions={REQUIRED_PERMISSIONS}>
        <Loading />
      </AppPage>
    );
  }

  return (
    <AppPage title={title} requiredPermissions={REQUIRED_PERMISSIONS}>
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <PageHeader
          title={title}
          subtitle="Encider Settings for the various output profiles used by the player, clip export, and WSC streamer."
          action={
            <Button variant="contained" endIcon={<Plus />} onClick={startCreate}>
              New Profile
            </Button>
          }
        />

        <Box
          sx={{
            display: 'grid',
            // Left (table) stays wide on a plain `1fr`; the right (detail/form)
            // is the shrinkable one. `minmax(0, …fr)` lets its fr value actually
            // win — a bare `fr` is `minmax(auto, 1fr)`, whose `auto` min refuses
            // to shrink below the panel's min-content, so ratio tweaks did nothing.
            gridTemplateColumns: { xs: '1fr', md: '1fr minmax(0, 0.7fr)', xl: '1fr minmax(0, 0.5fr)' },
            gap: 3,
            alignItems: 'start',
            mt: 3,
          }}
        >
          {/* ---- Left: profile list ---------------------------------------
              No Paper wrapper: MaterialReactTable brings its own surface, and
              nesting it inside an outlined Paper produced a panel-within-a-panel
              that none of the other data tables have. */}
          <MaterialReactTable
            columns={columns}
            data={profiles}
            enablePagination={false}
            enableRowActions
            positionActionsColumn="last"
            muiTopToolbarProps={{ sx: TOOLBAR_ACTIONS_SX }}
            // The heading and count the Paper used to carry, kept where the other
            // tables put their custom actions.
            renderTopToolbarCustomActions={() => (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 1 }}>
                <Typography variant="subtitle2">All Profiles</Typography>
                <Chip size="small" label={`${profiles.length} configured`} />
              </Stack>
            )}
            renderEmptyRowsFallback={() => (
              <Box sx={{ p: 2 }}>
                <Alert variant="outlined" severity="info">
                  No transcoding profiles yet. Create one with “New Profile”.
                </Alert>
              </Box>
            )}
            muiTableBodyRowProps={({ row }) => ({
              hover: true,
              selected: row.original.id === selectedId,
              onClick: () => selectProfile(row.original),
              sx: { cursor: 'pointer' },
            })}
            renderRowActions={({ row }) => {
              const used = row.original.assignedModes.length > 0;
              const usedBy = row.original.assignedModes
                .map((m) => MODE_LABELS[m] ?? m)
                .join(', ');
              return (
                <Box sx={ROW_ACTIONS_SX} onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => startEdit(row.original)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      used
                        ? `In use by ${usedBy} — reassign its targets to another profile before deleting`
                        : 'Delete'
                    }
                  >
                    {/* span keeps the tooltip working while the button is disabled */}
                    <span>
                      <IconButton
                        color="error"
                        disabled={used || deleteMutation.isPending}
                        onClick={() => handleDelete(row.original)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            }}
          />

          {/* ---- Right: detail (view) or form (edit/create) --------------- */}
          {/* minWidth: 0 releases the grid item's default `min-width: auto` so
              this panel can actually shrink below its content's min-content. */}
          <Box sx={{ minWidth: 0 }}>
            {mode === 'view' && selected && (
              <ProfileDetail profile={selected} onEdit={() => startEdit(selected)} />
            )}

            {mode === 'view' && !selected && (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Select a profile to view its details, or create a new one.
                </Typography>
              </Paper>
            )}

            {(mode === 'edit' || mode === 'create') && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {mode === 'create' ? 'New profile' : `Edit ${form.displayName || 'profile'}`}
                </Typography>

                <Stack spacing={2.5}>
                  <TextField
                    label="Display name"
                    value={form.displayName}
                    onChange={setField('displayName')}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Identifier"
                    value={form.name}
                    onChange={setField('name')}
                    required
                    fullWidth
                    helperText="Stable machine id (unique), e.g. tp_broadcast_1080"
                  />

                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Width"
                      type="number"
                      value={form.width}
                      onChange={setField('width')}
                      fullWidth
                    />
                    <TextField
                      label="Height"
                      type="number"
                      value={form.height}
                      onChange={setField('height')}
                      fullWidth
                    />
                  </Stack>

                  {/* Frame rate as numerator / denominator (broadcast convention). */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Frame rate
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                      <TextField
                        label="Numerator"
                        type="number"
                        value={form.fpsNum}
                        onChange={setField('fpsNum')}
                        sx={{ flex: 1 }}
                      />
                      <Typography variant="h5" color="text.secondary">
                        /
                      </Typography>
                      <TextField
                        label="Denominator"
                        type="number"
                        value={form.fpsDen}
                        onChange={setField('fpsDen')}
                        sx={{ flex: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                        = {fpsDecimal([Number(form.fpsNum), Number(form.fpsDen)])} fps
                      </Typography>
                    </Stack>
                  </Box>

                  <TextField
                    select
                    label="Tune"
                    value={form.tune}
                    onChange={setField('tune')}
                    fullWidth
                    SelectProps={{
                      // Keep the closed field compact: show only the label, not the
                      // multi-line description rendered inside each menu item.
                      renderValue: (value) =>
                        TUNE_META[value as string]?.label ?? (value as string),
                    }}
                  >
                    {/* Preserve an unrecognised stored value so it is not lost on save. */}
                    {form.tune !== '' && !TUNE_META[form.tune] && (
                      <MenuItem value={form.tune}>{form.tune}</MenuItem>
                    )}
                    {TUNE_OPTIONS.map((opt) => (
                      <MenuItem
                        key={opt.value}
                        value={opt.value}
                        sx={{ display: 'block', whiteSpace: 'normal', maxWidth: 380, py: 1 }}
                      >
                        <Typography variant="body2">{opt.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {opt.description}
                        </Typography>
                      </MenuItem>
                    ))}
                  </TextField>

                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Avg bitrate (bps)"
                      type="number"
                      value={form.avgBitrate}
                      onChange={setField('avgBitrate')}
                      fullWidth
                      helperText={
                        form.avgBitrate.trim() !== '' ? `= ${mbps(Number(form.avgBitrate))}` : ' '
                      }
                    />
                    <TextField
                      label="Max bitrate (bps)"
                      type="number"
                      value={form.maxBitrate}
                      onChange={setField('maxBitrate')}
                      fullWidth
                      helperText={
                        form.maxBitrate.trim() !== '' ? `= ${mbps(Number(form.maxBitrate))}` : ' '
                      }
                    />
                  </Stack>

                  {/* Advanced — encoder knobs beyond the common case. Collapsed by
                      default; each field is optional and omitted from `output`
                      when blank, so the player keeps its mode-specific default. */}
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">Advanced</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2.5}>
                        <TextField
                          select
                          label="Codec"
                          value={form.codec}
                          onChange={setField('codec')}
                          fullWidth
                          helperText="Output codec — leave unset to use the mode default"
                        >
                          <MenuItem value="">
                            <em>Mode default</em>
                          </MenuItem>
                          {/* Preserve an unrecognised stored codec so it is not lost on save. */}
                          {form.codec !== '' &&
                            !CODEC_OPTIONS.some((c) => c.value === form.codec) && (
                              <MenuItem value={form.codec}>{form.codec}</MenuItem>
                            )}
                          {CODEC_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>

                        <Stack direction="row" spacing={2}>
                          <TextField
                            label="Preset"
                            type="number"
                            value={form.preset}
                            onChange={setField('preset')}
                            fullWidth
                            inputProps={{ min: 1, max: 7 }}
                            InputProps={{
                              endAdornment: clearAdornment(form.preset !== '', clearField('preset')),
                            }}
                            helperText="1 (cheapest) – 7 (highest quality)"
                          />
                          <TextField
                            label="GOP length"
                            type="number"
                            value={form.gopLength}
                            onChange={setField('gopLength')}
                            fullWidth
                            inputProps={{ min: 0 }}
                            InputProps={{
                              endAdornment: clearAdornment(
                                form.gopLength !== '',
                                clearField('gopLength'),
                              ),
                            }}
                            helperText="Keyframe interval, in frames"
                          />
                        </Stack>

                        <TextField
                          label="P-frame interval (n_ps)"
                          type="number"
                          value={form.nPs}
                          onChange={setField('nPs')}
                          fullWidth
                          inputProps={{ min: 0 }}
                          InputProps={{
                            endAdornment: clearAdornment(form.nPs !== '', clearField('nPs')),
                          }}
                        />
                        {/* Frame-pattern reference for n_ps. Rendered as a <pre> with
                            monospace + whiteSpace: 'pre' so the columns line up — the
                            lines are joined with \n because JSX collapses literal
                            newlines in markup into single spaces. */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="pre"
                          sx={{ fontFamily: 'monospace', whiteSpace: 'pre', m: 0, mt: 0.5 }}
                        >
                          {[
                            'The interval between P-frames:',
                            '| value | gop(len=8)  |',
                            '|   0   |  IPPPPPPP   |',
                            '|   1   |  IPPPPPPP   |',
                            '|   2   |  IBPBPBPB   |',
                            '|   3   |  IBBPBBPB   |',
                          ].join('\n')}
                        </Typography>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Stack>

                <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end', mt: 3 }}>
                  <Button onClick={() => setMode('view')} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isFormValid || isSaving}
                  >
                    {mode === 'create' ? 'Create profile' : 'Save changes'}
                  </Button>
                </Stack>
              </Paper>
            )}
          </Box>
        </Box>
      </Container>
    </AppPage>
  );
};

// Read-only detail panel: derived stat cards + the raw profile JSON.
const ProfileDetail = ({
  profile,
  onEdit,
}: {
  profile: TranscodingProfile;
  onEdit: () => void;
}) => {
  const o = profile.output;
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h6">{profile.displayName ?? profile.name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {profile.name}
          </Typography>
        </Box>
         {profile.assignedModes.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            In use by:
          </Typography>
          {profile.assignedModes.map((m) => (
            <Chip key={m} size="small" variant="outlined" label={MODE_LABELS[m] ?? m} />
          ))}
        </Stack>
      )}
      </Stack>
        <TuneChip tune={o?.tune} />

     

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 2,
          mt: 2.5,
        }}
      >
        <StatCard
          label="Resolution"
          value={resolutionLabel(o)}
          sub={aspectRatio(o?.res?.width, o?.res?.height)}
        />
        <StatCard
          label="Frame rate"
          value={`${fpsDecimal(o?.fps)} fps`}
          sub={o?.fps && o.fps.length >= 2 ? `${o.fps[0]} / ${o.fps[1]}` : undefined}
        />
        <StatCard
          label="Avg bitrate"
          value={mbps(o?.avg_bitrate)}
          sub={o?.avg_bitrate != null ? `${o.avg_bitrate.toLocaleString()} bps` : undefined}
        />
        <StatCard
          label="Max bitrate"
          value={mbps(o?.max_bitrate)}
          sub={o?.max_bitrate != null ? `${o.max_bitrate.toLocaleString()} bps` : undefined}
        />
      </Box>

      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
        Profile JSON
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          mt: 1,
          p: 2,
          bgcolor: 'action.hover',
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: 13,
          overflow: 'auto',
        }}
      >
        {JSON.stringify(profile.output, null, 2)}
      </Box>

      <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="contained" startIcon={<EditIcon />} onClick={onEdit}>
          Edit
        </Button>
      </Stack>
    </Paper>
  );
};

export default ManageTranscodingProfilesPage;
